import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Lightbulb, Palette } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import logo from '../assets/logo_shinysmile_negative.png';
import LandingPage from '../pages/LandingPage';
import LoginPage from '../pages/LoginPage';
import RegisterPage from '../pages/RegisterPage';
import ResetPasswordPage from '../pages/ResetPasswordPage';
import ActivatePage from '../pages/ActivatePage';
import ClientDashboard from '../pages/ClientDashboard';
import ProsthetistDashboard from '../pages/ProsthetistDashboard';
import FeatureRequestModal from './common/FeatureRequestModal';
import UserDropdown from './common/UserDropdown';
import AccountSettingsModal from './common/AccountSettingsModal';
import { pendingNoticeFor } from '../config/notices';

const PAGE_TRANSITION_DURATION = 200;

// Bannière des notices one-shot (définies dans src/config/notices.js) :
// réservée aux comptes créés avant la feature annoncée, marquée vue dès
// l'affichage pour ne jamais réapparaître à une connexion suivante.
function NoticeBanner({ onOpenSettingsTab }) {
  const { user, dismissNotice } = useAuth();
  const [notice, setNotice] = useState(null);
  const shown = useRef(false);

  useEffect(() => {
    const pending = pendingNoticeFor(user);
    if (pending && !shown.current) {
      shown.current = true;
      setNotice(pending);
      dismissNotice(pending.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!notice) return null;

  return (
    <div className="glass border-b border-yellow-500/20 px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-3 animate-fade-in">
      <Palette className="h-5 w-5 text-yellow-400 flex-shrink-0" />
      <p className="text-sm text-gray-200 flex-1">{notice.message}</p>
      {notice.action && (
        <button
          onClick={() => { setNotice(null); onOpenSettingsTab(notice.action.settingsTab); }}
          className="text-sm text-yellow-400 hover:text-yellow-300 font-medium whitespace-nowrap"
        >
          {notice.action.label}
        </button>
      )}
      <button
        onClick={() => setNotice(null)}
        className="text-gray-400 hover:text-gray-200 transition-colors"
        aria-label="Fermer"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

function AppContent() {
  const [currentView, setCurrentView] = useState('landing');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [adminViewMode, setAdminViewMode] = useState('client'); // 'client' or 'prosthetist'
  const [featureRequestModalOpen, setFeatureRequestModalOpen] = useState(false);
  const [accountSettingsOpen, setAccountSettingsOpen] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState('profile');
  const [resetToken, setResetToken] = useState(null);
  const [activateToken, setActivateToken] = useState(null);
  const [isPageTransitioning, setIsPageTransitioning] = useState(false);
  const [pendingView, setPendingView] = useState(null);

  const {
    isAuthenticated,
    isInitializing,
    logout,
    isClient,
    isProsthetist,
    isAdmin,
    getUserName
  } = useAuth();

  // Vérifier si on est sur la page de réinitialisation de mot de passe au chargement
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const path = window.location.pathname;

    if (path === '/reset-password' && token) {
      setResetToken(token);
      setCurrentView('reset-password');
      // Nettoyer l'URL sans recharger
      window.history.replaceState({}, document.title, '/reset-password');
    } else if (path === '/activate' && token) {
      setActivateToken(token);
      setCurrentView('activate');
      window.history.replaceState({}, document.title, '/activate');
    }
  }, []);

  // Mettre à jour la vue en fonction de l'état d'authentification
  useEffect(() => {
    if (isInitializing) return; // Attendre la vérification de l'auth

    // Ne pas rediriger si on est sur reset-password ou activate
    if (currentView === 'reset-password' || currentView === 'activate') return;

    if (isAuthenticated && currentView !== 'dashboard') {
      setCurrentView('dashboard');
    } else if (!isAuthenticated && currentView === 'dashboard') {
      setCurrentView('landing');
    }
  }, [isAuthenticated, isInitializing, currentView]);

  const handleNavigation = (view) => {
    // Animate transition between public pages (landing, login, register)
    const publicPages = ['landing', 'login', 'register'];
    const isPublicTransition = publicPages.includes(currentView) && publicPages.includes(view);

    if (isPublicTransition && view !== currentView) {
      setIsPageTransitioning(true);
      setPendingView(view);
      setTimeout(() => {
        setCurrentView(view);
        setIsPageTransitioning(false);
        setPendingView(null);
      }, PAGE_TRANSITION_DURATION);
    } else {
      setCurrentView(view);
    }
    setMobileMenuOpen(false);
  };

  const handleLogin = (userData) => {
    // La gestion du login est maintenant dans le contexte
    // Cette fonction est gardée pour compatibilité avec les pages existantes
    setCurrentView('dashboard');
  };

  const handleLogout = async () => {
    await logout();
    setCurrentView('landing');
    setMobileMenuOpen(false);
  };

  const handleFeatureRequestSuccess = () => {
    // You could add a toast notification here later
    console.log('Feature request submitted successfully');
  };


  // Afficher un loader pendant la vérification de l'auth
  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Chargement...</p>
        </div>
      </div>
    );
  }

  // Page transition wrapper for public pages (opacity only - no scale to preserve fixed backgrounds)
  const pageTransitionClass = isPageTransitioning
    ? 'opacity-0 transition-opacity duration-200 ease-out'
    : 'opacity-100 transition-opacity duration-200 ease-out';

  // Show landing page, login or register without nav
  if (currentView === 'landing') {
    return (
      <div className={`min-h-screen ${pageTransitionClass}`}>
        <LandingPage onNavigate={handleNavigation} />
      </div>
    );
  }

  if (currentView === 'login') {
    return (
      <div className={`min-h-screen ${pageTransitionClass}`}>
        <LoginPage onNavigate={handleNavigation} onLogin={handleLogin} />
      </div>
    );
  }

  if (currentView === 'register') {
    return (
      <div className={`min-h-screen ${pageTransitionClass}`}>
        <RegisterPage onNavigate={handleNavigation} />
      </div>
    );
  }

  if (currentView === 'reset-password') {
    return <ResetPasswordPage onNavigate={handleNavigation} token={resetToken} />;
  }

  if (currentView === 'activate') {
    return <ActivatePage onNavigate={handleNavigation} token={activateToken} />;
  }

  // Main app with navigation (authenticated users)
  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="bg-black/30 border-b border-white/[0.07] backdrop-blur-xl sticky top-0 z-50">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center">
                <img src={logo} alt="ShinySmile" className="h-10 w-auto mr-3" />
                <div className="text-2xl font-bold text-white">
                  Shiny<span className="text-yellow-400">Smile</span>
                </div>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {/* Admin mode switcher */}
              {isAdmin() && (
                <div className="flex items-center space-x-2 glass border border-white/10 rounded-full p-1">
                  <button
                    onClick={() => setAdminViewMode('client')}
                    className={`px-3 py-1 rounded-full text-sm transition-all ${
                      adminViewMode === 'client'
                        ? 'bg-yellow-500 text-black shadow-lg'
                        : 'text-gray-400 hover:text-gray-200 hover:bg-white/15'
                    }`}
                  >
                    Vue Client
                  </button>
                  <button
                    onClick={() => setAdminViewMode('prosthetist')}
                    className={`px-3 py-1 rounded-full text-sm transition-all ${
                      adminViewMode === 'prosthetist'
                        ? 'bg-yellow-500 text-black shadow-lg'
                        : 'text-gray-400 hover:text-gray-200 hover:bg-white/15'
                    }`}
                  >
                    Vue Prothésiste
                  </button>
                </div>
              )}

              <UserDropdown
                userName={getUserName()}
                isAdmin={isAdmin()}
                onSettingsClick={() => setAccountSettingsOpen(true)}
                onLogoutClick={handleLogout}
              />
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-400 hover:text-white focus:outline-none focus:text-white transition-colors p-2 rounded-lg hover:bg-white/10"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-black/30 border-t border-white/10">
                <UserDropdown
                  userName={getUserName()}
                  isAdmin={isAdmin()}
                  onSettingsClick={() => {
                    setAccountSettingsOpen(true);
                    setMobileMenuOpen(false);
                  }}
                  onLogoutClick={handleLogout}
                />

                {/* Admin mode switcher for mobile */}
                {isAdmin() && (
                  <div className="px-3 py-2">
                    <div className="flex space-x-1 bg-white/10 border border-white/15 rounded-lg p-1">
                      <button
                        onClick={() => {
                          setAdminViewMode('client');
                          setMobileMenuOpen(false);
                        }}
                        className={`flex-1 px-3 py-2 rounded-md text-sm transition-all ${
                          adminViewMode === 'client'
                            ? 'bg-yellow-500 text-black shadow-lg'
                            : 'text-gray-400 hover:text-gray-200 hover:bg-white/15'
                        }`}
                      >
                        Vue Client
                      </button>
                      <button
                        onClick={() => {
                          setAdminViewMode('prosthetist');
                          setMobileMenuOpen(false);
                        }}
                        className={`flex-1 px-3 py-2 rounded-md text-sm transition-all ${
                          adminViewMode === 'prosthetist'
                            ? 'bg-yellow-500 text-black shadow-lg'
                            : 'text-gray-400 hover:text-gray-200 hover:bg-white/15'
                        }`}
                      >
                        Vue Prothésiste
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Notices one-shot pour les comptes antérieurs à une feature */}
      <NoticeBanner
        onOpenSettingsTab={(tab) => {
          setSettingsInitialTab(tab || 'profile');
          setAccountSettingsOpen(true);
        }}
      />

      {/* Main Content */}
      <main className="flex-1">
        {isClient() && <ClientDashboard />}
        {isProsthetist() && <ProsthetistDashboard />}
        {isAdmin() && adminViewMode === 'client' && <ClientDashboard />}
        {isAdmin() && adminViewMode === 'prosthetist' && <ProsthetistDashboard />}
      </main>

      {/* Floating Feature Request Button */}
      <button
        onClick={() => setFeatureRequestModalOpen(true)}
        className="fixed bottom-6 right-6 bg-yellow-500 hover:bg-yellow-600 text-black p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-50 group hover:scale-110"
        title="Suggérer une fonctionnalité"
      >
        <Lightbulb className="h-6 w-6" />

        {/* Tooltip */}
        <div className="absolute right-0 bottom-full mb-3 hidden group-hover:block">
          <div className="glass-deep border border-white/10 text-white text-sm px-3 py-2 rounded-lg whitespace-nowrap backdrop-blur-sm shadow-xl">
            Suggérer une fonctionnalité
            <div className="absolute top-full right-6 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
          </div>
        </div>
      </button>

      {/* Feature Request Modal */}
      <FeatureRequestModal
        isOpen={featureRequestModalOpen}
        onClose={() => setFeatureRequestModalOpen(false)}
        onSuccess={handleFeatureRequestSuccess}
      />

      {/* Account Settings Modal */}
      <AccountSettingsModal
        isOpen={accountSettingsOpen}
        onClose={() => {
          setAccountSettingsOpen(false);
          setSettingsInitialTab('profile');
        }}
        initialTab={settingsInitialTab}
      />
    </div>
  );
}

export default AppContent;